import axios from 'axios';

// Base path from Vite config (e.g., '/mba_coach/' in production, '/' in dev)
const basePath = import.meta.env.BASE_URL.replace(/\/$/, '');
export const uploadsUrl = (path) => `${basePath}/uploads/${path}`;

const api = axios.create({
  baseURL: `${basePath}/api`,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Token is added via auth store
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      const authStorage = localStorage.getItem('auth-storage');
      if (authStorage) {
        localStorage.removeItem('auth-storage');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export default api;

// API helper functions
export const chatApi = {
  sendMessage: (message, conversationId, fileIds = []) =>
    api.post('/chat', { message, conversationId, fileIds }),

  getConversations: (limit = 20, offset = 0) =>
    api.get(`/chat/conversations?limit=${limit}&offset=${offset}`),

  getConversation: (id) =>
    api.get(`/chat/conversations/${id}`),

  deleteConversation: (id) =>
    api.delete(`/chat/conversations/${id}`)
};

export const studentApi = {
  getProfile: () =>
    api.get('/student/profile'),

  updateProfile: (data) =>
    api.put('/student/profile', data),

  getUsage: () =>
    api.get('/student/usage'),

  getProgress: () =>
    api.get('/student/progress'),

  getDailyUsage: (days = 30) =>
    api.get(`/student/daily-usage?days=${days}`),

  getActivityCalendar: (days = 30) =>
    api.get(`/student/activity-calendar?days=${days}`)
};

export const adminApi = {
  getDashboard: () =>
    api.get('/admin/dashboard'),

  getStudents: (params = {}) =>
    api.get('/admin/students', { params }),

  createStudent: (data) =>
    api.post('/admin/students', data),

  grantL1Trial: (studentId) =>
    api.post(`/admin/students/${studentId}/l1`),

  updateStudentStatus: (studentId, status) =>
    api.put(`/admin/students/${studentId}/status`, { status }),

  getConfig: () =>
    api.get('/admin/config'),

  updateConfig: (data) =>
    api.put('/admin/config', data),

  getStudentsSummary: () =>
    api.get('/admin/dashboard/students-summary'),

  addStudentQuestions: (studentId, questions) =>
    api.post(`/admin/students/${studentId}/add-questions`, { questions }),

  getUsageBreakdown: () =>
    api.get('/admin/dashboard/usage-breakdown')
};

// File upload API
export const uploadApi = {
  uploadFiles: (files) => {
    const formData = new FormData();
    for (const file of files) {
      formData.append('files', file);
    }
    return api.post('/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  getFile: (id) =>
    api.get(`/upload/${id}`),

  getFileContent: (id) =>
    api.get(`/upload/${id}/content`),

  listFiles: (limit = 20, offset = 0) =>
    api.get(`/upload?limit=${limit}&offset=${offset}`),

  deleteFile: (id) =>
    api.delete(`/upload/${id}`),

  downloadFile: (id) =>
    api.get(`/upload/${id}/download`, { responseType: 'blob' })
};

// Share API
export const shareApi = {
  createShare: (conversationId, accessType = 'public', expiresIn = null) =>
    api.post('/share', { conversationId, accessType, expiresIn }),

  getSharedConversation: (token) =>
    api.get(`/share/${token}`),

  listShares: () =>
    api.get('/share'),

  revokeShare: (id) =>
    api.delete(`/share/${id}`),

  updateShare: (id, data) =>
    api.put(`/share/${id}`, data)
};

// Direct messaging API
export const messageApi = {
  getThreads: () =>
    api.get('/messages/threads'),

  createThread: (participantId) =>
    api.post('/messages/threads', { participantId }),

  getThread: (id, limit = 50, before = null) => {
    let url = `/messages/threads/${id}?limit=${limit}`;
    if (before) url += `&before=${before}`;
    return api.get(url);
  },

  sendMessage: (threadId, content) =>
    api.post(`/messages/threads/${threadId}`, { content }),

  markAsRead: (threadId) =>
    api.put(`/messages/threads/${threadId}/read`),

  poll: (since) =>
    api.get(`/messages/poll?since=${encodeURIComponent(since)}`),

  getUnreadCount: () =>
    api.get('/messages/unread-count'),

  getContacts: () =>
    api.get('/messages/contacts')
};

// Channel API (Open Chat)
export const channelApi = {
  getChannels: () =>
    api.get('/channels'),

  createChannel: (name, description, channelType = 'broadcast') =>
    api.post('/channels', { name, description, channelType }),

  getChannel: (id, limit = 50, before = null) => {
    let url = `/channels/${id}?limit=${limit}`;
    if (before) url += `&before=${before}`;
    return api.get(url);
  },

  postMessage: (channelId, content, messageType = 'text') =>
    api.post(`/channels/${channelId}/messages`, { content, messageType }),

  updateChannel: (id, data) =>
    api.put(`/channels/${id}`, data),

  deleteChannel: (id) =>
    api.delete(`/channels/${id}`),

  markAsRead: (id) =>
    api.put(`/channels/${id}/read`),

  addMembers: (channelId, userIds) =>
    api.post(`/channels/${channelId}/members`, { userIds }),

  removeMember: (channelId, userId) =>
    api.delete(`/channels/${channelId}/members/${userId}`),

  pinMessage: (channelId, messageId, pinned = true) =>
    api.put(`/channels/${channelId}/messages/${messageId}/pin`, { pinned }),

  poll: (channelId, since) =>
    api.get(`/channels/${channelId}/poll?since=${encodeURIComponent(since)}`)
};

// LMS API
export const lmsApi = {
  getCourses: (params = {}) =>
    api.get('/lms/courses', { params }),

  getCourse: (id) =>
    api.get(`/lms/courses/${id}`),

  createCourse: (data) =>
    api.post('/lms/courses', data),

  updateCourse: (id, data) =>
    api.put(`/lms/courses/${id}`, data),

  deleteCourse: (id) =>
    api.delete(`/lms/courses/${id}`),

  getLessons: (courseId) =>
    api.get(`/lms/courses/${courseId}/lessons`),

  getLesson: (id) =>
    api.get(`/lms/lessons/${id}`),

  createLesson: (data) =>
    api.post('/lms/lessons', data),

  updateLesson: (id, data) =>
    api.put(`/lms/lessons/${id}`, data),

  deleteLesson: (id) =>
    api.delete(`/lms/lessons/${id}`),

  updateProgress: (lessonId, data) =>
    api.put(`/lms/lessons/${lessonId}/progress`, data),

  updateTranscript: (lessonId, transcript) =>
    api.put(`/lms/lessons/${lessonId}/transcript`, { transcript }),

  triggerTranscription: (lessonId, audioFile) => {
    const formData = new FormData();
    if (audioFile) formData.append('audio', audioFile);
    return api.post(`/lms/lessons/${lessonId}/transcribe`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  getLiveSessions: (params = {}) =>
    api.get('/lms/live-sessions', { params }),

  getLiveSession: (id) =>
    api.get(`/lms/live-sessions/${id}`),

  createLiveSession: (data) =>
    api.post('/lms/live-sessions', data),

  updateLiveSession: (id, data) =>
    api.put(`/lms/live-sessions/${id}`, data),

  deleteLiveSession: (id) =>
    api.delete(`/lms/live-sessions/${id}`),

  getMyProgress: () =>
    api.get('/lms/my-progress')
};

// Assessment API
export const assessmentApi = {
  getAssessments: (params = {}) =>
    api.get('/assessments', { params }),

  getAssessment: (id) =>
    api.get(`/assessments/${id}`),

  createAssessment: (data) =>
    api.post('/assessments', data),

  updateAssessment: (id, data) =>
    api.put(`/assessments/${id}`, data),

  deleteAssessment: (id) =>
    api.delete(`/assessments/${id}`),

  addQuestion: (assessmentId, data) =>
    api.post(`/assessments/${assessmentId}/questions`, data),

  updateQuestion: (questionId, data) =>
    api.put(`/assessments/questions/${questionId}`, data),

  deleteQuestion: (questionId) =>
    api.delete(`/assessments/questions/${questionId}`),

  startAttempt: (assessmentId) =>
    api.post(`/assessments/${assessmentId}/attempt`),

  submitAttempt: (attemptId, responses) =>
    api.put(`/assessments/attempts/${attemptId}`, { responses }),

  getAttempt: (attemptId) =>
    api.get(`/assessments/attempts/${attemptId}`),

  getAttempts: (assessmentId) =>
    api.get(`/assessments/${assessmentId}/attempts`),

  gradeAttempt: (attemptId, data) =>
    api.put(`/assessments/attempts/${attemptId}/grade`, data),

  getMyAttempts: () =>
    api.get('/assessments/my-attempts'),

  generatePYQ: (data) =>
    api.post('/assessments/generate-pyq', data, { timeout: 300000 })
};

// Submission API
export const submissionApi = {
  create: (data) => {
    const formData = new FormData();
    formData.append('title', data.title);
    if (data.description) formData.append('description', data.description);
    if (data.lessonId) formData.append('lessonId', data.lessonId);
    if (data.assessmentId) formData.append('assessmentId', data.assessmentId);
    if (data.files) {
      for (const file of data.files) {
        formData.append('files', file);
      }
    }
    return api.post('/submissions', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  list: (params = {}) =>
    api.get('/submissions', { params }),

  get: (id) =>
    api.get(`/submissions/${id}`),

  update: (id, data) =>
    api.put(`/submissions/${id}`, data),

  delete: (id) =>
    api.delete(`/submissions/${id}`),

  review: (id, data) =>
    api.put(`/submissions/${id}/review`, data),

  downloadFile: (submissionId, fileId) =>
    api.get(`/submissions/${submissionId}/files/${fileId}/download`, { responseType: 'blob' })
};

// Category API
export const categoryApi = {
  list: () => api.get('/categories'),
  listAll: () => api.get('/categories/all'),
  getBySlug: (slug) => api.get(`/categories/${slug}`),
  create: (data) => api.post('/categories', data),
  update: (id, data) => api.put(`/categories/${id}`, data),
  delete: (id) => api.delete(`/categories/${id}`),
};

// Notebook API
export const notebookApi = {
  list: () => api.get('/notebooks'),

  get: (id) => api.get(`/notebooks/${id}`),

  create: (data) => {
    if (data.file) {
      const formData = new FormData();
      formData.append('file', data.file);
      if (data.title) formData.append('title', data.title);
      return api.post('/notebooks', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    }
    return api.post('/notebooks', { title: data.title, content: data.content });
  },

  update: (id, data) => api.put(`/notebooks/${id}`, data),

  delete: (id) => api.delete(`/notebooks/${id}`),

  chat: (id, message) => api.post(`/notebooks/${id}/chat`, { message }),

  clearChats: (id) => api.delete(`/notebooks/${id}/chats`),

  getArtifacts: (id) => api.get(`/notebooks/${id}/artifacts`),

  getArtifact: (id, type) => api.get(`/notebooks/${id}/artifacts/${type}`),

  extractTopics: (id) => api.post(`/notebooks/${id}/topics`, {}, { timeout: 60000 }),
  generate: (id, type, selectedTopics) => api.post(`/notebooks/${id}/generate/${type}`, { selectedTopics }, { timeout: 120000 }),

  deleteArtifact: (id, type) => api.delete(`/notebooks/${id}/artifacts/${type}`)
};

// Coupon API
export const couponApi = {
  list: () => api.get('/coupons'),
  create: (data) => api.post('/coupons', data),
  update: (id, data) => api.put(`/coupons/${id}`, data),
  delete: (id) => api.delete(`/coupons/${id}`),
  validate: (code) => api.post('/coupons/validate', { code }),
};

// Auth API
export const authApi = {
  changePassword: (currentPassword, newPassword) =>
    api.post('/auth/change-password', { currentPassword, newPassword }),
  completeOnboarding: () =>
    api.post('/auth/complete-onboarding')
};

// Notification API
export const materialApi = {
  list: (params = {}) => api.get('/materials', { params }),
  upload: (formData) => api.post('/materials', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update: (id, data) => api.put(`/materials/${id}`, data),
  delete: (id) => api.delete(`/materials/${id}`),
  trackDownload: (id) => api.post(`/materials/${id}/download`),
};

export const notificationApi = {
  list: (params = {}) =>
    api.get('/notifications', { params }),

  getUnreadCount: () =>
    api.get('/notifications/unread-count'),

  markRead: (id) =>
    api.put(`/notifications/${id}/read`),

  markAllRead: () =>
    api.put('/notifications/read-all'),

  delete: (id) =>
    api.delete(`/notifications/${id}`),

  getPreferences: () =>
    api.get('/notifications/preferences'),

  updatePreferences: (data) =>
    api.put('/notifications/preferences', data)
};
